import { useCallback, useEffect, useState } from "react";
import { ObservableQuery } from "@apollo/client";
import { PageInfo } from "../__generated__/graphql";
import { useSnackbar } from "./snackbar";
import { useI18n } from "./i18n-hooks";

// Before using usePagination with GraphQL call,
// make sure that you added a "merge" function for your call in `graphql.config.js`

// The list query result may carry extra fields (e.g. `count`) alongside
// `elements`/`pageInfo`; only those two are needed here.
type PaginatedResult = { elements: unknown[]; pageInfo: PageInfo };

type UsePaginationParams<TData extends Record<string, PaginatedResult>> = {
    fetchMore: ObservableQuery<TData, { offset: number }>["fetchMore"];
};

export const usePagination = <TData extends Record<string, PaginatedResult>>({
    fetchMore: fetchMoreProps,
}: UsePaginationParams<TData>) => {
    const [isLoadingData, setIsLoadingData] = useState(false);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [nextOffset, setNextOffset] = useState(0);
    const { showSnackbar } = useSnackbar();
    const i18n = useI18n();

    const fetchMore = useCallback(async () => {
        try {
            setIsLoadingData(true);
            const newResult = await fetchMoreProps({ variables: { offset: nextOffset } });
            if (newResult.data) {
                const key = Object.keys(newResult.data)[0] as keyof TData;
                setHasNextPage(newResult.data[key]?.pageInfo?.hasNextPage || false);
                setNextOffset(newResult.data[key]?.pageInfo?.nextOffset || 0);
            } else {
                setHasNextPage(false);
                setNextOffset(0);
            }
        } catch (_e) {
            showSnackbar({
                text: i18n.t("errors.pagination_error"),
                mode: "error",
            });
        } finally {
            setIsLoadingData(false);
        }
    }, [fetchMoreProps, i18n, nextOffset, showSnackbar]);

    useEffect(() => {
        void fetchMore();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return { isLoading: isLoadingData, fetchMore, hasNextPage };
};
